import { createSelector } from 'reselect';
import {
  PROFILE_OLD_PATHS,
  PROFILE_PATHS,
} from 'applications/personalization/profile/constants';
import { MY_HEALTH_URLS, MY_VA_BASE, MY_VA_PATHS } from 'platform/my-va/paths';
import { toggleValues } from 'platform/site-wide/feature-toggles/selectors';
import FEATURE_FLAG_NAMES from 'platform/utilities/feature-toggles/featureFlagNames';
import {
  isInMPI,
  isProfileLoading,
  selectProfile,
} from 'platform/user/selectors';

// The header hides the My VA and Profile links a user can't open. The
// component asks menuItemFilter about each link; the rules live here, keyed by
// href. header/tests/menuAccess.unit.spec.jsx checks every href below is still
// in the component's packaged menu.

// The component's hrefs and the path constants differ by trailing slashes.
export const normalizeHref = href => href?.replace(/\/+$/, '') || '/';

// All a user without full access sees: the pages that don't need a verified
// identity (design's list, DS #7207). Profile's own side nav agrees
// (routesForNav.js marks only these two Profile pages requiresLOA3: false).
export const LIMITED_PAGES = new Set(
  [
    `${MY_VA_BASE}${MY_VA_PATHS.HOME}`,
    MY_HEALTH_URLS.HOME,
    `${MY_VA_BASE}${MY_VA_PATHS.FORMS}`,
    `${MY_VA_BASE}${MY_VA_PATHS.FORMS_SUBMISSIONS}`,
    PROFILE_OLD_PATHS.SIGNIN_INFORMATION,
    PROFILE_PATHS.SIGNED_IN_DEVICES,
  ].map(normalizeHref),
);

// The pages still behind a rollout toggle. Remove an entry when its toggle
// is retired.
export const FLAGGED_PAGES = {
  [PROFILE_PATHS.PASSKEYS]: FEATURE_FLAG_NAMES.identityPasskeyEnabled,
  [PROFILE_PATHS.ACCREDITED_REPRESENTATIVE]:
    FEATURE_FLAG_NAMES.representativeStatusEnableV2Features,
};

// Limited for unverified users and users not found in MPI.
export const selectMenuAccessLevel = state =>
  selectProfile(state).loa?.current === 3 && isInMPI(state)
    ? 'full'
    : 'limited';

// Passed to the header as menuItemFilter. A group is always kept: the
// component hides one whose children are all hidden. While the profile loads,
// access isn't known, so nothing is hidden for it; the menu trims for a
// limited user once it arrives. A flagged page is hidden until the toggles
// load, then only when its toggle is false, so a retired toggle hides nothing.
// A failed toggle request loads with no flags at all, but the header itself
// is off then (App reads my_va_bluesky_enabled), so no flagged link leaks.
export const selectMenuItemFilter = createSelector(
  isProfileLoading,
  selectMenuAccessLevel,
  toggleValues,
  (profileLoading, accessLevel, toggles) => item => {
    if (item.children) return true;
    const href = normalizeHref(item.href);
    if (
      !profileLoading &&
      accessLevel === 'limited' &&
      !LIMITED_PAGES.has(href)
    )
      return false;
    const toggle = FLAGGED_PAGES[href];
    return !toggle || (toggles.loading === false && toggles[toggle] !== false);
  },
);
